import React, { useEffect, useState, useCallback } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import antivirusService from "../../services/antivirusService";

const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const PAGE_SIZE = 10;

const statusMeta = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("completed") || s.includes("clean"))
    return { text: "Clean",       icon: "shield",         color: "var(--vz-success)", bg: "rgba(var(--vz-success-rgb),.1)", border: "rgba(var(--vz-success-rgb),.3)" };
  if (s.includes("failed") || s.includes("error"))
    return { text: "Failed",      icon: "x-circle",       color: "var(--vz-danger)",  bg: "rgba(var(--vz-danger-rgb),.1)",  border: "rgba(var(--vz-danger-rgb),.3)"  };
  if (s.includes("running") || s.includes("scanning"))
    return { text: "Scanning",    icon: "loader",         color: "var(--vz-info)",    bg: "rgba(var(--vz-info-rgb),.1)",    border: "rgba(var(--vz-info-rgb),.3)"    };
  if (s.includes("interrupted") || s.includes("warning"))
    return { text: "Interrupted", icon: "alert-triangle", color: "var(--vz-warning)", bg: "rgba(var(--vz-warning-rgb),.1)", border: "rgba(var(--vz-warning-rgb),.3)" };
  if (s.includes("pending"))
    return { text: "Pending",     icon: "clock",          color: "var(--vz-info)",    bg: "rgba(var(--vz-info-rgb),.1)",    border: "rgba(var(--vz-info-rgb),.3)"    };
  return   { text: status || "Unknown", icon: "help-circle", color: "var(--vz-secondary-color)", bg: "rgba(var(--vz-secondary-rgb),.1)", border: "var(--vz-border-color)" };
};

const formatFileSize = (mb) => {
  if (!mb) return "—";
  const num = parseFloat(mb);
  if (isNaN(num)) return "—";
  return num >= 1000 ? `${(num / 1000).toFixed(1)} GB` : `${num.toFixed(0)} MB`;
};

const formatDuration = (duration) => {
  if (!duration) return "—";
  const match = duration.match(/\(([^)]+)\)/);
  return match ? match[1] : duration;
};

const ScanHistory = () => {
  const [scans,       setScans]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page,        setPage]        = useState(1);
  const [search,      setSearch]      = useState("");

  const loadScans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await antivirusService.getAgentScans();
      if (res?.success && res.scans) {
        setScans(res.scans);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error("Failed to load scan history", err);
      setScans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScans();
    const t = setInterval(loadScans, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [loadScans]);

  const filtered = scans.filter(s =>
    !search || (s.agent_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (s.agent_ip || "").includes(search)
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageScans = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

  return (
    <div style={card}>
      {/* Header */}
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)" }}>Agent Antivirus Status</span>
          <p style={{ color: "var(--vz-secondary-color)", fontSize: 12, margin: 0 }}>
            {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : "Real-time protection state of managed endpoints"}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ position: "relative" }}>
            <FeatherIcon icon="search" size={13} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--vz-secondary-color)", pointerEvents: "none" }} />
            <input
              value={search}
              onChange={handleSearch}
              placeholder="Search agent..."
              style={{
                paddingLeft: 30, paddingRight: 10, height: 32, borderRadius: 8, fontSize: 12,
                border: "1px solid var(--vz-border-color)", background: "var(--vz-secondary-bg)",
                color: "var(--vz-body-color)", outline: "none", width: 180
              }}
            />
          </div>
          <button onClick={loadScans} disabled={loading}
            style={{ height: 32, padding: "0 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", background: "transparent", color: "var(--vz-primary)", border: "1px solid var(--vz-primary)", display: "flex", alignItems: "center", gap: 4 }}>
            <FeatherIcon icon="refresh-cw" size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "48px 24px", textAlign: "center" }}>
          <Spinner color="primary" />
          <p style={{ marginTop: 8, color: "var(--vz-secondary-color)", fontSize: 13 }}>Loading scan data...</p>
        </div>
      ) : scans.length === 0 ? (
        <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--vz-secondary-color)" }}>
          <FeatherIcon icon="shield" size={36} style={{ opacity: 0.25, display: "block", margin: "0 auto 12px" }} />
          <p style={{ margin: 0, fontSize: 14 }}>No scan data available.</p>
        </div>
      ) : (
        <>
          {/* Summary bar */}
          <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", gap: 28, background: "rgba(var(--vz-primary-rgb),.02)" }}>
            {[
              { label: "Agents",            value: scans.length,                                                color: "var(--vz-body-color)" },
              { label: "Clean",             value: scans.filter(s => (s.infected_files || 0) === 0).length,    color: "var(--vz-success)"    },
              { label: "Threats",           value: scans.filter(s => (s.infected_files || 0) > 0).length,      color: "var(--vz-danger)"     },
              { label: "Total Files Scanned", value: scans.reduce((a, s) => a + (s.files_scanned || 0), 0).toLocaleString(), color: "var(--vz-info)" },
            ].map((s, i) => (
              <div key={i}>
                <span style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</span>
                <span style={{ color: "var(--vz-secondary-color)", fontSize: 12, marginLeft: 5 }}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Agent", "IP Address", "Last Scan", "Status", "Files Scanned", "Data Scanned", "Duration", "Threats"].map((h, i) => (
                    <th key={i} style={{
                      padding: "10px 18px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: ".06em", color: "var(--vz-secondary-color)", textAlign: "left",
                      borderBottom: "1px solid var(--vz-border-color)",
                      background: "var(--vz-light, rgba(0,0,0,.02))", whiteSpace: "nowrap"
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageScans.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: "32px 18px", textAlign: "center", color: "var(--vz-secondary-color)", fontSize: 13 }}>
                      No agents match your search.
                    </td>
                  </tr>
                ) : pageScans.map((s) => {
                  const sm = statusMeta(s.scan_status);
                  const hasThreats = (s.infected_files || 0) > 0;
                  return (
                    <tr key={s.id}
                      style={{ borderBottom: "1px solid var(--vz-border-color)" }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(var(--vz-primary-rgb),.03)"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}
                    >
                      <td style={{ padding: "12px 18px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <FeatherIcon icon="monitor" size={14} style={{ color: "var(--vz-info)" }} />
                          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--vz-body-color)" }}>
                            {s.agent_name || "Unknown"}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: "12px 18px" }}>
                        <code style={{ fontSize: 12, color: "var(--vz-secondary-color)", fontFamily: "monospace" }}>
                          {s.agent_ip || "—"}
                        </code>
                      </td>
                      <td style={{ padding: "12px 18px", fontSize: 12, color: "var(--vz-secondary-color)" }}>
                        {s.timestamp
                          ? new Date(s.timestamp).toLocaleString([], { dateStyle: "short", timeStyle: "short" })
                          : "Never"}
                      </td>
                      <td style={{ padding: "12px 18px" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                          color: sm.color, background: sm.bg, border: `1px solid ${sm.border}`
                        }}>
                          <FeatherIcon icon={sm.icon} size={11} />{sm.text}
                        </span>
                      </td>
                      <td style={{ padding: "12px 18px", textAlign: "center" }}>
                        <span style={{
                          padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                          background: "rgba(var(--vz-primary-rgb),.08)", color: "var(--vz-primary)"
                        }}>
                          {s.files_scanned > 0 ? s.files_scanned.toLocaleString() : "—"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 18px", textAlign: "center", fontSize: 12, color: "var(--vz-secondary-color)" }}>
                        {formatFileSize(s.data_scanned)}
                      </td>
                      <td style={{ padding: "12px 18px", textAlign: "center", fontSize: 12, color: "var(--vz-secondary-color)" }}>
                        {formatDuration(s.scan_duration)}
                      </td>
                      <td style={{ padding: "12px 18px" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                          color: hasThreats ? "var(--vz-danger)" : "var(--vz-success)",
                          background: hasThreats ? "rgba(var(--vz-danger-rgb),.1)" : "rgba(var(--vz-success-rgb),.1)",
                          border: hasThreats ? "1px solid rgba(var(--vz-danger-rgb),.3)" : "1px solid rgba(var(--vz-success-rgb),.3)"
                        }}>
                          <FeatherIcon icon={hasThreats ? "alert-triangle" : "shield"} size={11} />
                          {hasThreats ? `${s.infected_files} Found` : "Clean"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer: count + pagination */}
          <div style={{ padding: "10px 18px", borderTop: "1px solid var(--vz-border-color)", background: "rgba(var(--vz-primary-rgb),.02)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
              Showing <strong style={{ color: "var(--vz-primary)" }}>{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)}</strong> of <strong style={{ color: "var(--vz-primary)" }}>{filtered.length}</strong> agents
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button onClick={() => setPage(1)} disabled={currentPage === 1}
                style={{ ...pgBtn, opacity: currentPage === 1 ? 0.4 : 1 }}>
                <FeatherIcon icon="chevrons-left" size={13} />
              </button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                style={{ ...pgBtn, opacity: currentPage === 1 ? 0.4 : 1 }}>
                <FeatherIcon icon="chevron-left" size={13} />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === "..." ? (
                  <span key={`ellipsis-${i}`} style={{ fontSize: 12, color: "var(--vz-secondary-color)", padding: "0 4px" }}>…</span>
                ) : (
                  <button key={p} onClick={() => setPage(p)}
                    style={{ ...pgBtn, fontWeight: p === currentPage ? 700 : 400, background: p === currentPage ? "var(--vz-primary)" : "transparent", color: p === currentPage ? "#fff" : "var(--vz-body-color)", borderColor: p === currentPage ? "var(--vz-primary)" : "var(--vz-border-color)" }}>
                    {p}
                  </button>
                ))}

              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                style={{ ...pgBtn, opacity: currentPage === totalPages ? 0.4 : 1 }}>
                <FeatherIcon icon="chevron-right" size={13} />
              </button>
              <button onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
                style={{ ...pgBtn, opacity: currentPage === totalPages ? 0.4 : 1 }}>
                <FeatherIcon icon="chevrons-right" size={13} />
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const pgBtn = {
  minWidth: 30, height: 30, borderRadius: 6, fontSize: 12, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: "1px solid var(--vz-border-color)", background: "transparent",
  color: "var(--vz-body-color)", padding: "0 6px"
};

export default ScanHistory;
